import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  Botao,
  Campo,
  CelulaDeAcoes,
  EstadoErro,
  EstadoVazio,
  MenuDeAcoes,
  Selo,
  Tabela,
  useDialogo,
  type ColunaTabela,
} from '../componentes/ui';
import { useIdentidade } from '../hooks/useSessao';
import { AvisoDoLote, BotaoDoLote, useAcaoEmLote } from '../componentes/AcaoEmLote';
import { BotaoExtrairXlsx } from '../componentes/BotaoExtrairXlsx';
import { requisicaoAutenticada } from '../servicos/http';
import { resumirNomes } from '../servicos/lote';
import { formatarCpf } from '../servicos/formato';
import { diaLocal, todasAsPaginas, type ColunaDaPlanilha } from '../servicos/planilha';
import { formatarTelefone } from './vinculos-trabalhistas';
import { Pagina } from './Carteira';
import { CadastroDeColaborador } from './cliente/FormulariosDeColaborador';
import { ErroDaApi } from './ErroDaApi';

export interface VinculoDaPessoa {
  readonly businessId: string;
  readonly razaoSocial: string;
  readonly papel: 'Gestor' | 'Colaborador';
  readonly ocupacao: string | null;
  readonly ativo: boolean;
}

export interface PessoaResumo {
  readonly personId: string;
  readonly accountId: string | null;
  readonly nome: string;
  readonly emailAddress: string;
  readonly cpf: string | null;
  readonly telefone: string | null;
  readonly ativo: boolean;
  readonly temAcesso: boolean;
  readonly ultimoAcesso: string | null;
  readonly vinculos: readonly VinculoDaPessoa[];
}

interface PaginaDePessoas {
  readonly itens: readonly PessoaResumo[];
  readonly total: number;
  readonly pagina: number;
  readonly porPagina: number;
}

const POR_PAGINA = 25;

/** As colunas da tabela em valores de planilha; os vínculos, um por um, separados por `;`. */
const COLUNAS_DA_PLANILHA: readonly ColunaDaPlanilha<PessoaResumo>[] = [
  { titulo: 'Pessoa', largura: 32, valor: (p) => p.nome },
  { titulo: 'E-mail', largura: 32, valor: (p) => p.emailAddress },
  { titulo: 'CPF', largura: 16, valor: (p) => (p.cpf === null ? 'Sem CPF' : formatarCpf(p.cpf)) },
  { titulo: 'Telefone', largura: 16, valor: (p) => formatarTelefone(p.telefone) },
  { titulo: 'Acesso', largura: 12, valor: (p) => (p.temAcesso ? 'Ativo' : 'Sem acesso') },
  { titulo: 'Último acesso', formato: 'data', valor: (p) => diaLocal(p.ultimoAcesso) },
  {
    titulo: 'Empresas',
    largura: 60,
    valor: (p) =>
      p.vinculos
        .map((v) => `${v.razaoSocial} (${v.papel}${v.ocupacao === null ? '' : ` — ${v.ocupacao}`})`)
        .join('; '),
  },
];

/**
 * RN-311 — Pessoas e acessos, o catálogo que atravessa as empresas.
 *
 * ## A pergunta que ela responde
 *
 * A aba de colaboradores diz quem trabalha numa empresa. Esta diz o inverso:
 * **em quantas empresas esta pessoa está**. Uma pessoa aqui chega a ter dez
 * vínculos — um escritório contábil que atende dez clientes —, e no Deca 1
 * descobrir isso exigia abrir empresa por empresa.
 *
 * ## Por que a paginação é do servidor
 *
 * São 554 pessoas com os vínculos de cada uma. Trazer tudo para filtrar no
 * navegador seria uma resposta grande para desenhar 25 linhas, e a busca por
 * nome/CPF/e-mail precisa varrer o conjunto inteiro — não a página atual.
 *
 * ## "Adicionar pessoa" é o cadastro de colaborador, com a empresa opcional
 *
 * O formulário é o mesmo da aba Colaboradores — com a checagem de CPF que
 * reaproveita quem já existe. Sem empresa, a pessoa fica só na agenda, sem
 * acesso; o vínculo se faz depois, no cadastro dela (`PessoaDetalhe`).
 */
export function Pessoas(): JSX.Element {
  const navegar = useNavigate();
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [cadastroAberto, setCadastroAberto] = useState(false);
  const dialogo = useDialogo();
  const identidade = useIdentidade();
  const queryClient = useQueryClient();

  // Item 7 de 15/09 — exclusão lógica: a pessoa, as contas e os vínculos
  // ficam inativos. Ela some daqui e das empresas; o histórico fica.
  const excluir = useMutation({
    mutationFn: (personId: string) =>
      requisicaoAutenticada<void>(`/business/pessoas/${personId}`, { metodo: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pessoas'] });
      void queryClient.invalidateQueries({ queryKey: ['colaboradores'] });
    },
  });
  // A mesma exclusão, para as pessoas marcadas.
  const excluirMarcadas = useAcaoEmLote<PessoaResumo>({
    acao: (p) =>
      requisicaoAutenticada<void>(`/business/pessoas/${p.personId}`, { metodo: 'DELETE' }),
    nome: (p) => p.nome,
    aoTerminar: () => {
      void queryClient.invalidateQueries({ queryKey: ['pessoas'] });
      void queryClient.invalidateQueries({ queryKey: ['colaboradores'] });
    },
  });

  const consulta = useQuery({
    queryKey: ['pessoas', busca, pagina],
    queryFn: () =>
      requisicaoAutenticada<PaginaDePessoas>(
        `/business/pessoas?pagina=${pagina}&porPagina=${POR_PAGINA}${
          busca.trim() === '' ? '' : `&busca=${encodeURIComponent(busca.trim())}`
        }`,
      ),
    // Sem isto a tabela pisca vazia a cada tecla digitada na busca.
    placeholderData: keepPreviousData,
  });

  const colunas: readonly ColunaTabela<PessoaResumo>[] = [
    {
      chave: 'nome',
      titulo: 'Pessoa',
      larguraMinima: '20rem',
      celula: (p) => (
        <span className="flex flex-col">
          <span className="text-sm text-conteudo-primario">{p.nome}</span>
          <span className="valor-fiscal text-xs text-conteudo-sutil">{p.emailAddress}</span>
        </span>
      ),
    },
    {
      chave: 'cpf',
      titulo: 'CPF',
      larguraMinima: '10rem',
      celula: (p) =>
        p.cpf === null ? (
          // O marcador da migração não vira número na tela: quem tem CPF em
          // branco no cadastro precisa ver isso, não um código.
          <span className="text-xs text-conteudo-sutil">Sem CPF</span>
        ) : (
          <span className="valor-fiscal whitespace-nowrap text-sm">{formatarCpf(p.cpf)}</span>
        ),
    },
    {
      chave: 'acesso',
      titulo: 'Acesso',
      larguraMinima: '9rem',
      celula: (p) => (
        <span className="flex flex-col gap-1">
          <Selo tom={p.temAcesso ? 'positivo' : 'neutro'}>
            {p.temAcesso ? 'Ativo' : 'Sem acesso'}
          </Selo>
          {p.ultimoAcesso !== null && (
            <span className="text-xs text-conteudo-sutil">
              Entrou em {new Date(p.ultimoAcesso).toLocaleDateString('pt-BR')}
            </span>
          )}
        </span>
      ),
    },
    {
      chave: 'vinculos',
      titulo: 'Empresas',
      larguraMinima: '24rem',
      celula: (p) =>
        p.vinculos.length === 0 ? (
          <span className="text-xs text-conteudo-sutil">Nenhum vínculo</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {p.vinculos.slice(0, 4).map((v) => (
              <Link
                key={`${v.businessId}-${v.papel}`}
                to={`/empresas/${v.businessId}`}
                // A linha abre a pessoa; o chip abre a empresa — sem isto os
                // dois navegavam, e a pessoa ganhava.
                onClick={(e) => e.stopPropagation()}
                className="rounded border border-borda px-2 py-0.5 text-xs text-conteudo-secundario transition-colors hover:border-acento-ciano hover:text-acento-ciano focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
                title={`${v.papel}${v.ocupacao === null ? '' : ` — ${v.ocupacao}`}`}
              >
                {v.razaoSocial}
              </Link>
            ))}
            {p.vinculos.length > 4 && (
              <span className="px-1 py-0.5 text-xs text-conteudo-sutil">
                +{p.vinculos.length - 4}
              </span>
            )}
          </span>
        ),
    },
    {
      chave: 'acoes',
      titulo: 'Ações',
      tituloOculto: true,
      larguraMinima: '3rem',
      celula: (p) => (
        <CelulaDeAcoes>
          <MenuDeAcoes
            rotulo={p.nome}
            itens={[
              { rotulo: 'Abrir', aoEscolher: () => navegar(`/pessoas/${p.personId}`) },
              {
                rotulo: 'Excluir',
                perigo: true,
                desabilitado: !p.ativo || identidade?.personId === p.personId,
                descricao: !p.ativo
                  ? 'Já inativa.'
                  : identidade?.personId === p.personId
                    ? 'Você não pode excluir a si mesmo.'
                    : 'Desativa a pessoa, o acesso e os vínculos.',
                aoEscolher: () => {
                  void (async () => {
                    const ok = await dialogo.confirmar({
                      titulo: `Excluir ${p.nome}?`,
                      descricao:
                        'A pessoa deixa de entrar na plataforma e sai de todas as empresas em que está. O que ela já fez continua registrado.',
                      rotuloConfirmar: 'Excluir',
                      tom: 'perigo',
                    });
                    if (ok) excluir.mutate(p.personId);
                  })();
                },
              },
            ]}
          />
        </CelulaDeAcoes>
      ),
    },
  ];

  if (consulta.isError) {
    return (
      <Pagina titulo="Pessoas e acessos">
        <EstadoErro
          titulo="Não foi possível carregar as pessoas"
          mensagem={consulta.error.message}
          aoTentarNovamente={() => void consulta.refetch()}
        />
      </Pagina>
    );
  }

  const dados = consulta.data;
  const total = dados?.total ?? 0;
  const ultimaPagina = Math.max(1, Math.ceil(total / POR_PAGINA));

  return (
    <Pagina
      titulo="Pessoas e acessos"
      descricao="Quem tem cadastro, em quais empresas e com que papel. Use antes de criar um acesso: a mesma pessoa costuma já estar aqui."
      contagem={total}
      acao={
        <div className="flex flex-wrap items-center gap-3">
          <Campo
            rotulo="Buscar pessoa"
            rotuloOculto
            type="search"
            placeholder="Nome, e-mail ou CPF"
            className="w-full sm:w-72"
            value={busca}
            onChange={(e) => {
              setBusca(e.target.value);
              setPagina(1);
            }}
          />
          {/* Todas as pessoas, não a busca: a lista é paginada no servidor. */}
          <BotaoExtrairXlsx
            titulo="Pessoas e acessos"
            colunas={COLUNAS_DA_PLANILHA}
            carregar={() =>
              todasAsPaginas((pagina) =>
                requisicaoAutenticada<PaginaDePessoas>(
                  `/business/pessoas?pagina=${pagina}&porPagina=100`,
                ),
              )
            }
          />
          <Botao onClick={() => setCadastroAberto(true)}>Adicionar pessoa</Botao>
        </div>
      }
    >
      {!consulta.isPending && (dados?.itens.length ?? 0) === 0 ? (
        <EstadoVazio
          titulo="Nenhuma pessoa encontrada"
          descricao={
            busca.trim() === ''
              ? 'Não há pessoas cadastradas.'
              : `Nada corresponde a "${busca.trim()}". A busca cobre nome, e-mail e CPF.`
          }
        />
      ) : (
        <>
          {excluir.isError && <ErroDaApi titulo="Não foi possível excluir" erro={excluir.error} />}
          <AvisoDoLote
            desfecho={excluirMarcadas.desfecho}
            feitos={(n) => (n === 1 ? '1 pessoa excluída' : `${n} pessoas excluídas`)}
            aoFechar={excluirMarcadas.limparDesfecho}
          />
          <Tabela
            legenda="Pessoas cadastradas e seus vínculos"
            colunas={colunas}
            dados={dados?.itens ?? []}
            chaveDaLinha={(p) => p.personId}
            chaveDeCabecalhoDaLinha="nome"
            carregando={consulta.isPending}
            aoClicarNaLinha={(p) => navegar(`/pessoas/${p.personId}`)}
            selecao={{
              substantivo: { singular: 'pessoa', plural: 'pessoas', feminino: true },
              nomeDaLinha: (p) => p.nome,
              // As mesmas travas do "Excluir" da linha.
              bloqueio: (p) =>
                !p.ativo
                  ? 'Já inativa.'
                  : identidade?.personId === p.personId
                    ? 'Você não pode excluir a si mesmo.'
                    : null,
              acoes: (marcadas, limpar) => (
                <BotaoDoLote
                  rotulo="Excluir"
                  perigo
                  marcadas={marcadas}
                  limpar={limpar}
                  lote={excluirMarcadas}
                  confirmacao={(m) => ({
                    titulo:
                      m.length === 1 ? `Excluir ${m[0]!.nome}?` : `Excluir ${m.length} pessoas?`,
                    descricao: `${resumirNomes(m.map((p) => p.nome))}.\n\nCada uma deixa de entrar na plataforma e sai de todas as empresas em que está. O que já fizeram continua registrado.`,
                    rotuloConfirmar: 'Excluir',
                    tom: 'perigo',
                  })}
                />
              ),
            }}
          />

          {ultimaPagina > 1 && (
            <nav
              aria-label="Paginação"
              className="flex flex-wrap items-center justify-between gap-3"
            >
              <span className="text-sm text-conteudo-secundario">
                Página {pagina} de {ultimaPagina}
              </span>
              <span className="flex gap-2">
                <Botao
                  variante="secundario"
                  tamanho="pequeno"
                  disabled={pagina === 1}
                  onClick={() => setPagina((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </Botao>
                <Botao
                  variante="secundario"
                  tamanho="pequeno"
                  disabled={pagina >= ultimaPagina}
                  onClick={() => setPagina((p) => p + 1)}
                >
                  Próxima
                </Botao>
              </span>
            </nav>
          )}
        </>
      )}

      <CadastroDeColaborador aberto={cadastroAberto} aoFechar={() => setCadastroAberto(false)} />
    </Pagina>
  );
}
